/**
 * RecordClientDecisionModal — "Record client decision" on a proposal (TeamSection.jsx).
 *
 * Staff log what the client decided after being shown the proposal outside the app (email/call/
 * meeting) — Approved / Revision requested / Declined, an optional note, and a date. No
 * client-facing page or signature; see docs/TEAM.md's "internal log only" decision.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { recordProposalClientDecision, CLIENT_DECISION_LABELS } from '../services/consultantRfps.js'

const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

function todayIso() { return new Date().toISOString().slice(0, 10) }

export default function RecordClientDecisionModal({ proposal, onClose, onRecorded }) {
  const [decision, setDecision] = useState(proposal.client_decision || 'approved')
  const [decidedAt, setDecidedAt] = useState(proposal.client_decision_at ? String(proposal.client_decision_at).slice(0, 10) : todayIso())
  const [note, setNote] = useState(proposal.client_decision_note || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  if (!proposal) return null

  async function submit() {
    setSaving(true); setError('')
    try {
      await recordProposalClientDecision(proposal.id, decision, note.trim() || null, decidedAt ? new Date(decidedAt).toISOString() : null)
      onRecorded()
    } catch (e) {
      setError((e && e.message) || 'Could not save the decision.')
      setSaving(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="RecordClientDecisionModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 400, maxWidth: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>Record client decision</div>
          <button onClick={onClose} disabled={saving} style={{ background: 'transparent', border: 'none', cursor: saving ? 'default' : 'pointer', color: 'var(--aj-text-muted)', padding: 4, opacity: saving ? 0.5 : 1 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div style={{ padding: '14px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={label}>Decision</label>
            <select style={input} value={decision} disabled={saving} onChange={e => setDecision(e.target.value)}>
              {Object.entries(CLIENT_DECISION_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div>
            <label style={label}>Date</label>
            <input type="date" style={input} value={decidedAt} disabled={saving} onChange={e => setDecidedAt(e.target.value)} />
          </div>

          <div>
            <label style={label}>Note (optional)</label>
            <textarea style={{ ...input, minHeight: 60, resize: 'vertical', fontFamily: '"DM Sans",sans-serif' }} value={note} disabled={saving}
              placeholder="e.g. how the client responded, any conditions…" onChange={e => setNote(e.target.value)} />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={onClose} disabled={saving} style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save decision'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
